import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Gift, Phone, MapPin, Eye, EyeOff, Check, X as XIcon, Send } from "lucide-react";
import { COUNTRIES, COUNTRY_BY_CODE } from "@/lib/countries";

const CORRIDOR_OPTIONS = [
  { code: "tg_ga", label: "Togo → Gabon", emoji: "🇹🇬 → 🇬🇦" },
  { code: "ga_fr", label: "Gabon → France", emoji: "🇬🇦 → 🇫🇷" },
  { code: "ga_rw", label: "Gabon → Rwanda", emoji: "🇬🇦 → 🇷🇼" },
  { code: "fr_ga", label: "France → Gabon", emoji: "🇫🇷 → 🇬🇦" },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);
  const [country, setCountry] = useState("");
  const [phone, setPhone] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [preferredCorridor, setPreferredCorridor] = useState("");
  const [loading, setLoading] = useState(false);
  const [referralDiscount, setReferralDiscount] = useState(5);

  useEffect(() => {
    api.get("/referral-config-public").then(({ data }) => {
      if (data?.discount_percent != null) setReferralDiscount(data.discount_percent);
    }).catch(() => {});
  }, []);

  const dial = useMemo(() => (country ? COUNTRY_BY_CODE[country]?.dial || "" : ""), [country]);
  const pwdMatch = password.length >= 6 && password === confirmPassword;
  const pwdMismatch = confirmPassword.length > 0 && password !== confirmPassword;

  useEffect(() => {
    const ref = (params.get("ref") || localStorage.getItem("sl_ref") || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (ref) setReferralCode(ref);
  }, [params]);

  // Auto-suggest a preferred corridor based on the selected country (only if user hasn't picked one yet).
  useEffect(() => {
    if (preferredCorridor) return;
    if (country === "TG") setPreferredCorridor("tg_ga");
    else if (country === "GA") setPreferredCorridor("ga_fr");
    else if (country === "FR") setPreferredCorridor("fr_ga");
    // No auto-suggest for RW or others (user picks manually).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setLoading(true);
    try {
      const phoneClean = phone ? (dial && !phone.startsWith("+") ? `${dial}${phone.replace(/^0+/, "")}` : phone) : null;
      await register(name, email, password, referralCode || null, {
        phone: phoneClean,
        country,
        preferred_corridor: preferredCorridor || null,
      });
      toast.success(referralCode ? "Compte créé — parrain enregistré !" : "Compte créé !");
      navigate("/mes-transactions");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen sl-hero-bg">
      <Header />
      <div className="pt-32 pb-20">
        <div className="max-w-md mx-auto px-4">
          <h1 className="font-heading text-3xl font-bold">Créer un compte</h1>
          <p className="text-gray-600 mt-1">Suivez tous vos envois en un seul endroit.</p>
          <form onSubmit={submit} className="sl-card mt-6 space-y-4" data-testid="register-form">
            <div>
              <label className="sl-label">Nom complet</label>
              <input className="sl-input" required value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name" />
            </div>
            <div>
              <label className="sl-label">Email</label>
              <input className="sl-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email" />
            </div>
            <div>
              <label className="sl-label flex items-center gap-1.5">
                <MapPin size={13} className="text-[var(--sl-primary)]" />
                Pays d&apos;origine
              </label>
              <select className="sl-input" required value={country} onChange={(e) => setCountry(e.target.value)} data-testid="register-country">
                <option value="">— Sélectionner —</option>
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.emoji} {c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="sl-label flex items-center gap-1.5">
                <Phone size={13} className="text-[var(--sl-primary)]" />
                Téléphone
              </label>
              <div className="flex gap-2">
                {dial && <span className="inline-flex items-center justify-center px-3 rounded-md bg-gray-100 text-gray-700 text-sm font-mono whitespace-nowrap" data-testid="register-dial">{dial}</span>}
                <input className="sl-input" type="tel" required inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^0-9+ ]/g, ""))} placeholder={dial ? "Ex. 612345678" : "Ex. +33612345678"} data-testid="register-phone" />
              </div>
            </div>
            <div>
              <label className="sl-label flex items-center gap-1.5">
                <Send size={13} className="text-[var(--sl-primary)]" />
                Corridor préféré <span className="text-xs text-gray-400 font-normal">(optionnel)</span>
              </label>
              <select
                className="sl-input"
                value={preferredCorridor}
                onChange={(e) => setPreferredCorridor(e.target.value)}
                data-testid="register-preferred-corridor"
              >
                <option value="">— Aucun (choisir à chaque envoi) —</option>
                {CORRIDOR_OPTIONS.map((c) => (
                  <option key={c.code} value={c.code}>{c.emoji}  {c.label}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Nous pré-sélectionnerons ce corridor au démarrage de chaque envoi pour vous éviter un clic.
              </p>
            </div>
            <div>
              <label className="sl-label">Mot de passe (6 caractères minimum)</label>
              <div className="relative">
                <input
                  className="sl-input pr-10"
                  type={showPwd ? "text" : "password"}
                  minLength={6}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 p-1"
                  aria-label={showPwd ? "Masquer" : "Afficher"}
                  data-testid="register-password-toggle"
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label className="sl-label">Confirmer le mot de passe</label>
              <div className="relative">
                <input
                  className={`sl-input pr-10 ${pwdMismatch ? "border-red-300 focus:ring-red-200" : ""}`}
                  type={showPwd2 ? "text" : "password"}
                  minLength={6}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  data-testid="register-password-confirm"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd2((v) => !v)}
                  className="absolute right-9 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 p-1"
                  aria-label={showPwd2 ? "Masquer" : "Afficher"}
                  data-testid="register-password-confirm-toggle"
                >
                  {showPwd2 ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
                {confirmPassword.length > 0 && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2" data-testid="register-password-match-icon">
                    {pwdMatch ? <Check size={16} className="text-emerald-600" /> : <XIcon size={16} className="text-red-500" />}
                  </span>
                )}
              </div>
              {pwdMismatch && (
                <p className="mt-1 text-xs text-red-600" data-testid="register-password-mismatch">
                  Les mots de passe ne correspondent pas.
                </p>
              )}
            </div>
            <div>
              <label className="sl-label flex items-center gap-1.5">
                <Gift size={13} className="text-[var(--sl-primary)]" />
                Code de parrainage <span className="text-xs text-gray-400 font-normal">(optionnel)</span>
              </label>
              <input
                className="sl-input font-mono tracking-wider uppercase"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                placeholder="Ex. MARIE2026"
                maxLength={20}
                data-testid="register-referral"
              />
              {referralCode && (
                <p className="mt-1.5 text-xs text-emerald-700">
                  Vous bénéficierez d&apos;une remise de {referralDiscount} % sur les frais des transferts Gabon → France et Gabon → Rwanda.
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={loading || pwdMismatch || password.length < 6}
              className="sl-btn-primary w-full"
              data-testid="register-submit"
            >
              {loading ? "Création…" : "Créer mon compte"}
            </button>
            <div className="text-sm text-center text-gray-500">
              Déjà un compte ? <Link to="/login" className="text-[var(--sl-primary)] font-medium">Connexion</Link>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
}
